import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { settingApi } from '../../services/api';

export default function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    settingApi.list()
      .then(res => {
        const groups = res.data.data || {};
        const flat = {};
        Object.values(groups).forEach(items => {
          items.forEach(item => { flat[item.key] = item.value; });
        });
        setSettings(flat);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const payload = Object.entries(settings).map(([key, value]) => ({ key, value }));
      await settingApi.update(payload);
      setMessage('✅ Pengaturan berhasil disimpan!');
    } catch (err) {
      setMessage('❌ Gagal menyimpan pengaturan.');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const FIELD_GROUPS = {
    general: [
      { key: 'site_name', label: 'Nama Situs', type: 'text' },
      { key: 'site_tagline', label: 'Tagline', type: 'text' },
      { key: 'site_description', label: 'Deskripsi', type: 'richtext' },
      { key: 'footer_text', label: 'Teks Footer', type: 'richtext' },
      { key: 'site_logo', label: 'Logo URL', type: 'text' },
      { key: 'site_favicon', label: 'Favicon URL', type: 'text' },
    ],
    appearance: [
      { key: 'primary_color', label: 'Warna Utama (Background & Judul)', type: 'color', hint: 'Digunakan untuk background header, section gelap, dan warna judul.', defaultValue: '#081626' },
      { key: 'secondary_color', label: 'Warna Aksen (CTA & Subjudul)', type: 'color', hint: 'Digunakan untuk tombol CTA, highlight, badge, link, dan warna subjudul.', defaultValue: '#00CFFF' },
      { key: 'surface_color', label: 'Warna Permukaan (Kartu & Latar)', type: 'color', hint: 'Digunakan untuk latar belakang kartu, section terang, dan area konten.', defaultValue: '#FFFFFF' },
      { key: 'text_color', label: 'Warna Teks Konten', type: 'color', hint: 'Digunakan untuk teks isi/paragraf di seluruh halaman.', defaultValue: '#081626' },
    ],
    contact: [
      { key: 'contact_email', label: 'Email', type: 'text' },
      { key: 'contact_phone', label: 'Telepon', type: 'text' },
      { key: 'contact_address', label: 'Alamat', type: 'textarea' },
      { key: 'whatsapp_number', label: 'No. WhatsApp', type: 'text' },
    ],
    social: [
      { key: 'facebook_url', label: 'Facebook URL', type: 'text' },
      { key: 'instagram_url', label: 'Instagram URL', type: 'text' },
      { key: 'youtube_url', label: 'YouTube URL', type: 'text' },
      { key: 'tiktok_url', label: 'TikTok URL', type: 'text' },
      { key: 'twitter_url', label: 'Twitter URL', type: 'text' },
    ],
    homepage: [
      { key: 'home_hero_title', label: 'Hero Title', type: 'text' },
      { key: 'home_hero_subtitle', label: 'Hero Subtitle', type: 'textarea' },
    ],
    seo: [
      { key: 'google_analytics', label: 'Google Analytics ID', type: 'text' },
    ],
  };

  if (loading) {
    return <AdminLayout active="Pengaturan"><div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;
  }

  return (
    <AdminLayout active="Pengaturan">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-gray-800 mb-1">Pengaturan</h1>
        <button onClick={handleSave} disabled={saving} className="bg-brand text-brand-dark px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-brand-dark disabled:opacity-50 transition-colors">
          {saving ? 'Menyimpan...' : 'Simpan Semua'}
        </button>
      </div>

      {message && <div className={`mb-4 p-3 rounded-xl text-sm ${message.includes('✅') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>{message}</div>}

      <div className="space-y-6">
        {Object.entries(FIELD_GROUPS).map(([groupKey, fields]) => (
          <div key={groupKey} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 capitalize">{groupKey === 'seo' ? 'SEO' : groupKey}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.map(field => (
                <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  {field.type === 'richtext' ? (
                    <RichTextEditor
                      value={settings[field.key] || ''}
                      onChange={(val) => handleChange(field.key, val)}
                      height={200}
                      placeholder={`Tulis ${field.label.toLowerCase()}...`}
                    />
                  ) : field.type === 'textarea' ? (
                    <textarea
                      rows={3}
                      value={settings[field.key] || ''}
                      onChange={e => handleChange(field.key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand outline-none resize-none"
                    />
                  ) : field.type === 'color' ? (
                    <div>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={settings[field.key] || field.defaultValue || '#081626'}
                          onChange={e => handleChange(field.key, e.target.value)}
                          className="w-12 h-10 border border-gray-200 rounded-lg cursor-pointer"
                        />
                        <input
                          type="text"
                          value={settings[field.key] || ''}
                          onChange={e => handleChange(field.key, e.target.value)}
                          placeholder={field.defaultValue || '#081626'}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand outline-none font-mono"
                        />
                      </div>
                      {field.hint && <p className="text-xs text-gray-400 mt-1">{field.hint}</p>}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={settings[field.key] || ''}
                      onChange={e => handleChange(field.key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-brand outline-none"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}